import ExcelJS from 'exceljs';
import { ManifoldPlanReportData } from '../ReportGeneratorTypes';
import { generateReportHeaderExcel } from './reportHeader';
import { cargoColors, getCargoColor } from 'src/utils/stowage/cargoColorsUtils';
import { getFormattedCargoNumber } from 'src/utils/stowage/cargoOperationUtils';
import { getLetterForNumber, setCargoColor } from '../excelReportUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';

const DEFAULT_MANIFOLD_WIDTH = 10;

/**
 * Generates a manifold plan report for the given vessel and cargo content
 * @param workBook - ExcelJS workbook instance
 * @param data - Report data containing vessel and cargo content information
 * @param logoImageId - ID of the logo image in the workbook
 **/
export const generateManifoldPlanReport = (workBook: ExcelJS.Workbook, data: ManifoldPlanReportData, logoImageId: number) => {
    const loadingCargo = data.cargoContent.filter((cargoContent) => cargoContent.cargoOperation?.operationType === 'Loading');
    const unloadingCargo = data.cargoContent.filter((cargoContent) => cargoContent.cargoOperation?.operationType === 'Unloading');

    const loadingWorksheet = workBook.addWorksheet('Manifold Plan - Loading');
    generateManifoldPlanReportPage(loadingWorksheet, data, loadingCargo, logoImageId, 'Loading');
    const unloadingWorksheet = workBook.addWorksheet('Manifold Plan - Unloading');
    generateManifoldPlanReportPage(unloadingWorksheet, data, unloadingCargo, logoImageId, 'Unloading');
};

const generateManifoldPlanReportPage = (
    worksheet: ExcelJS.Worksheet,
    data: ManifoldPlanReportData,
    cargoContent: CargoContent[],
    logoImageId: number,
    conditionString: string = 'Loading'
) => {
    const title = `Manifold Plan - ${conditionString} in ${data.selectedPortCall?.portDisplayName} (${data.selectedBerthCall?.berthName})`;

    generateReportHeaderExcel(worksheet, data.vessel.vesselName, title, data.selectedCharterers, logoImageId);

    let currentRow = 8;

    for (const tank of data.vessel.cargoTanks.sort((a, b) => (a.orderInManifoldPlan ?? 0) - (b.orderInManifoldPlan ?? 0))) {
        const manifoldRowWidth = tank.widthInManifoldPlan ?? DEFAULT_MANIFOLD_WIDTH;

        const cargoInTank = cargoContent.filter(
            (cargoContent) =>
                (cargoContent.cargoOperation?.operationType === 'Loading' || cargoContent.cargoOperation?.operationType === 'Unloading') &&
                cargoContent.stowage.tankId === tank.tankId
        );

        const startCol = manifoldRowWidth === 8 ? 2 : 1;

        const startCell = worksheet.getCell(`${getLetterForNumber(startCol)}${currentRow}`);
        startCell.value = tank.tankName;

        const endCell = worksheet.getCell(`${getLetterForNumber(startCol + manifoldRowWidth)}${currentRow}`);
        endCell.value = tank.tankName;

        const centerMergeCellStart = worksheet.getCell(`${getLetterForNumber(startCol + 1)}${currentRow}`);

        worksheet.mergeCells(`${centerMergeCellStart.address}:${getLetterForNumber(startCol + manifoldRowWidth - 1)}${currentRow}`);
        worksheet.getRow(currentRow + 1).height = 5;
        setBorders(startCell, centerMergeCellStart, endCell);

        if (
            !cargoInTank ||
            cargoInTank.length === 0 ||
            (data.selectedTankIds && data.selectedTankIds.length > 0 && !data.selectedTankIds.includes(tank.tankId))
        ) {
            currentRow += 2;
            continue;
        }

        centerMergeCellStart.value = `#${getFormattedCargoNumber(cargoInTank[0].cargo.voyageNumber, cargoInTank[0].cargo.cargoNumber, false)} ${
            cargoInTank[0].cargo.product.tradeName
        }`;

        const cargoColor = getCargoColor(cargoInTank[0].cargo) ?? cargoColors[0];
        setCargoColor(startCell, cargoColor);
        setCargoColor(centerMergeCellStart, cargoColor);
        setCargoColor(endCell, cargoColor);

        startCell.alignment = { vertical: 'middle', horizontal: 'left' };
        centerMergeCellStart.alignment = { vertical: 'middle', horizontal: 'center' };
        endCell.alignment = { vertical: 'middle', horizontal: 'right' };

        currentRow += 2;
    }
};

const setBorders = (startCell, mergedCell, endCell) => {
    startCell.border = {
        top: { style: 'medium' },
        left: { style: 'medium' },
        bottom: { style: 'medium' },
    };

    mergedCell.border = {
        top: { style: 'medium' },
        bottom: { style: 'medium' },
    };

    endCell.border = {
        top: { style: 'medium' },
        bottom: { style: 'medium' },
        right: { style: 'medium' },
    };
};
